import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, t, tp } from '../utils/translations';

// Create Translation Context
const TranslationContext = createContext();

// Translation Provider Component
export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState('ar'); // Default to Arabic
  const [direction, setDirection] = useState('rtl');

  // Update document direction when language changes
  useEffect(() => {
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    setDirection(dir);
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
    
    // Update body class for styling
    document.body.className = document.body.className.replace(/\b(rtl|ltr)\b/g, '');
    document.body.classList.add(dir);
  }, [language]);

  // Translation function
  const translate = (key, fallback = key) => {
    return t(key, fallback);
  };

  // Plural translation function
  const translatePlural = (key, count, fallback = key) => {
    return tp(key, count, fallback);
  };

  // Change language function
  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('preferred-language', newLanguage);
  };

  // Get available languages
  const getAvailableLanguages = () => {
    return [
      { code: 'ar', name: 'العربية', nativeName: 'العربية' },
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'fr', name: 'Français', nativeName: 'Français' }
    ];
  };

  // Format date according to language
  const formatDate = (date, options = {}) => {
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    });
  };

  // Format time according to language
  const formatTime = (date, options = {}) => {
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(date).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      ...options
    });
  };

  // Format number according to language
  const formatNumber = (number, options = {}) => {
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  };

  // Format currency according to language
  const formatCurrency = (amount, currency = 'TND', options = {}) => {
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...options
    }).format(amount);
  };

  // Get text direction
  const isRTL = () => direction === 'rtl';
  const isLTR = () => direction === 'ltr';

  // Get language-specific class names
  const getDirectionClass = (baseClass = '') => {
    return `${baseClass} ${direction}`.trim();
  };

  // Get language-specific font family
  const getFontFamily = () => {
    switch (language) {
      case 'ar':
        return "'Cairo', 'Tajawal', sans-serif";
      case 'en':
        return "'Inter', 'Roboto', sans-serif";
      case 'fr':
        return "'Inter', 'Roboto', sans-serif";
      default:
        return "'Cairo', 'Tajawal', sans-serif";
    }
  };

  // Get language-specific text alignment
  const getTextAlign = () => {
    return language === 'ar' ? 'right' : 'left';
  };

  // Initialize language from localStorage or browser preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    const browserLanguage = navigator.language.split('-')[0];
    
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else if (['ar', 'en', 'fr'].includes(browserLanguage)) {
      setLanguage(browserLanguage);
    } else {
      setLanguage('ar'); // Default to Arabic
    }
  }, []);

  const value = {
    language,
    direction,
    translations,
    translate,
    t: translate, // Alias for convenience
    translatePlural,
    tp: translatePlural, // Alias for convenience
    changeLanguage,
    getAvailableLanguages,
    formatDate,
    formatTime,
    formatNumber,
    formatCurrency,
    isRTL,
    isLTR,
    getDirectionClass,
    getFontFamily,
    getTextAlign
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use translation context
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// HOC for components that need translation
export const withTranslation = (Component) => {
  return function TranslatedComponent(props) {
    const translation = useTranslation();
    return <Component {...props} translation={translation} />;
  };
};

// Helper component for conditional rendering based on language
export const LanguageSwitch = ({ ar, en, fr, fallback = null }) => {
  const { language } = useTranslation();
  
  switch (language) {
    case 'ar':
      return ar || fallback;
    case 'en':
      return en || fallback;
    case 'fr':
      return fr || fallback;
    default:
      return ar || fallback;
  }
};

// Helper component for RTL/LTR conditional rendering
export const DirectionSwitch = ({ rtl, ltr }) => {
  const { isRTL } = useTranslation();
  return isRTL() ? rtl : ltr;
};

// Translation component for inline translations
export const T = ({ k, fallback, count, ...props }) => {
  const { translate, translatePlural } = useTranslation();
  
  const text = count !== undefined 
    ? translatePlural(k, count, fallback)
    : translate(k, fallback);
    
  return <span {...props}>{text}</span>;
};

export default TranslationContext;

